import React from 'react'
import AppCombobox from '../AppCombobox'

export default function AcademicCurriculumSection({
  // Academic Year
  showAcademicYear = true,
  academicYear = '',
  onAcademicYearChange,
  academicYearOptions = [],

  // Ignore Duplication
  showIgnoreDuplication = false,
  ignoreDuplication = false,
  onIgnoreDuplicationChange,

  // Course
  showCourse = true,
  course = '',
  onCourseChange,
  courseOptions = [],

  // Subject
  showSubject = true,
  subject = '',
  onSubjectChange,
  subjectOptions = [],

  // Chapter No & Topic
  showChapterNo = false,
  chapterNo = 0,
  onChapterNoChange,
  onSelectChapter,

  showTopic = false,
  topic = '',
  onTopicChange,
  topicLabel = 'Topic',
  topicPlaceholder = 'Enter session topic name...',

  // Hours
  showHours = false,
  hours = 1,
  onHoursChange,

  // Reference Book & Video URL
  showReferenceBook = false,
  referenceBook = '',
  onReferenceBookChange,

  showVideoUrl = false,
  videoUrl = '',
  onVideoUrlChange,
}) {
  return (
    <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
      {/* ── Header ── */}
      <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <span className="material-symbols-outlined text-lg">school</span>
        </div>
        <div>
          <h2 className="font-display text-base font-bold text-on-surface">
            Academic &amp; Curriculum
          </h2>
          <p className="text-xs text-on-surface-variant">
            Define academic year, course curriculum, and syllabus details.
          </p>
        </div>
      </div>

      {/* ── Fields ── */}
      <div className="p-6 md:p-8 flex flex-col gap-5 text-xs">
        
        {/* Academic Year (+ optional Ignore Duplication) */}
        {showAcademicYear && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Academic Year
            </span>
            <div className={`col-span-12 ${showIgnoreDuplication ? 'sm:col-span-5' : 'sm:col-span-9'}`}>
              <AppCombobox
                options={academicYearOptions}
                value={academicYear}
                onChange={onAcademicYearChange}
                placeholder="Select Academic Year..."
                triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold"
              />
            </div>
            {showIgnoreDuplication && (
              <div className="col-span-12 sm:col-span-4">
                <label className="inline-flex items-center gap-2 rounded-xl bg-surface-container-low px-3.5 py-2 border border-outline-variant/20 cursor-pointer select-none text-xs font-bold text-on-surface transition hover:bg-surface-container-high">
                  <input
                    type="checkbox"
                    checked={ignoreDuplication}
                    onChange={(e) => onIgnoreDuplicationChange?.(e.target.checked)}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary"
                  />
                  <span>Ignore Duplication</span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Course */}
        {showCourse && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Course<span className="text-error font-bold"> *</span>
            </span>
            <div className="col-span-12 sm:col-span-9">
              <AppCombobox
                options={courseOptions}
                value={course}
                onChange={onCourseChange}
                placeholder="Select Course..."
                triggerClassName="h-10 w-full bg-surface-container-low text-xs font-bold text-primary"
              />
            </div>
          </div>
        )}

        {/* Subject */}
        {showSubject && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Subject<span className="text-error font-bold"> *</span>
            </span>
            <div className="col-span-12 sm:col-span-9">
              <AppCombobox
                options={subjectOptions}
                value={subject}
                onChange={onSubjectChange}
                placeholder="Select Subject..."
                triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold"
              />
            </div>
          </div>
        )}

        {/* Chapter No. & Topic */}
        {(showChapterNo || showTopic) && (
          <div className="grid grid-cols-12 gap-4 items-center">
            {showChapterNo && (
              <>
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Chapter No.<span className="text-error font-bold"> *</span>
                </span>
                <div className="col-span-12 sm:col-span-4 flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={chapterNo}
                    onChange={(e) => onChapterNoChange?.(parseInt(e.target.value, 10) || 0)}
                    className="h-10 w-24 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-xs font-bold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition"
                  />
                  {onSelectChapter && (
                    <button
                      type="button"
                      onClick={onSelectChapter}
                      className="h-10 px-4 rounded-xl bg-surface-container-high hover:bg-outline-variant/40 border border-outline-variant/20 text-xs font-bold text-primary transition cursor-pointer select-none active:scale-95"
                    >
                      Select
                    </button>
                  )}
                </div>
              </>
            )}

            {showTopic && (
              <>
                <span
                  className={`col-span-12 ${
                    showChapterNo ? 'sm:col-span-1 text-left sm:text-center' : 'sm:col-span-3 text-left'
                  } text-xs font-black uppercase tracking-wider text-on-surface-variant`}
                >
                  {topicLabel}<span className="text-error font-bold"> *</span>
                </span>
                <div className={`col-span-12 ${showChapterNo ? 'sm:col-span-4' : 'sm:col-span-9'}`}>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => onTopicChange?.(e.target.value)}
                    placeholder={topicPlaceholder}
                    className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-xs font-medium text-on-surface outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Teaching Hours */}
        {showHours && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Hours (Est.)<span className="text-error font-bold"> *</span>
            </span>
            <div className="col-span-12 sm:col-span-4 flex items-center gap-2">
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={hours}
                onChange={(e) => onHoursChange?.(parseFloat(e.target.value) || 1)}
                className="h-10 w-24 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-xs font-bold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition"
              />
              <span className="text-xs font-semibold text-on-surface-variant">teaching hours</span>
            </div>
          </div>
        )}

        {/* Reference Book */}
        {showReferenceBook && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Reference Book
            </span>
            <div className="col-span-12 sm:col-span-9">
              <input
                type="text"
                value={referenceBook}
                onChange={(e) => onReferenceBookChange?.(e.target.value)}
                placeholder="Author / Publication / Textbook title..."
                className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-xs font-medium text-on-surface outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition"
              />
            </div>
          </div>
        )}

        {/* Video / Online URL */}
        {showVideoUrl && (
          <div className="grid grid-cols-12 gap-4 items-center">
            <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
              Online / Video URL
            </span>
            <div className="col-span-12 sm:col-span-9">
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => onVideoUrlChange?.(e.target.value)}
                placeholder="https://..."
                className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-xs font-medium text-on-surface outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition"
              />
            </div>
          </div>
        )}

      </div>
    </section>
  )
}
